import { GeckoComponentDataFrame } from 'types'

export const WORKFLOW_GENERATED_BY_PLAN_REPAIRS = 'plan-repairs'

const ROLODEX_INTERNAL_ACCESS_RESTRICTION = 'internal'
const ROLODEX_VISIBLE_ACCESS_RESTRICTION = null

interface ReusableWorkflowGeneratedCdfMetadata {
  displayName?: string
  originPlanId?: string
}

// Session-generated short-term layers only receive `forecast_generated_by`
// when they are canonicalized/persisted, so pre-save copies are identified
// by their generated id shape instead.
const SESSION_SHORT_TERM_GENERATED_ID_PREFIXES = [
  'short-term-corrosion-rate-',
  'short-term-remaining-life-',
] as const

const PLAN_REPAIR_GENERATED_BY_PARAM = 'forecast_generated_by'
const PLAN_REPAIR_DISPLAY_NAME_PARAM = 'forecast_display_name'
const PLAN_REPAIR_ORIGIN_PLAN_ID_PARAM = 'forecast_origin_plan_id'

export function isWorkflowGeneratedCdf(cdf: GeckoComponentDataFrame): boolean {
  const params = getCdfParams(cdf)

  return (
    params[PLAN_REPAIR_GENERATED_BY_PARAM] ===
      WORKFLOW_GENERATED_BY_PLAN_REPAIRS ||
    SESSION_SHORT_TERM_GENERATED_ID_PREFIXES.some((prefix) =>
      cdf.id.startsWith(prefix)
    )
  )
}

export function isReusableWorkflowCdf(cdf: GeckoComponentDataFrame): boolean {
  return (
    getCdfParams(cdf)[PLAN_REPAIR_GENERATED_BY_PARAM] ===
      WORKFLOW_GENERATED_BY_PLAN_REPAIRS &&
    cdf.raw.access_restriction !== ROLODEX_INTERNAL_ACCESS_RESTRICTION
  )
}

export function isVisibleDataLayerOption(
  cdf: GeckoComponentDataFrame
): boolean {
  return !isWorkflowGeneratedCdf(cdf) || isReusableWorkflowCdf(cdf)
}

export function getWorkflowCdfDisplayName(
  cdf: GeckoComponentDataFrame
): string | undefined {
  const displayName = getCdfParams(cdf)[PLAN_REPAIR_DISPLAY_NAME_PARAM]
  return typeof displayName === 'string' && displayName.trim()
    ? displayName.trim()
    : undefined
}

export function withReusableWorkflowGeneratedCdfMetadata(
  cdf: GeckoComponentDataFrame,
  metadata: ReusableWorkflowGeneratedCdfMetadata
): GeckoComponentDataFrame {
  return withWorkflowGeneratedCdfMetadata(cdf, {
    ...metadata,
    accessRestriction: ROLODEX_VISIBLE_ACCESS_RESTRICTION,
  })
}

export function withInternalWorkflowGeneratedCdfMetadata(
  cdf: GeckoComponentDataFrame,
  metadata: Pick<ReusableWorkflowGeneratedCdfMetadata, 'originPlanId'> = {}
): GeckoComponentDataFrame {
  return withWorkflowGeneratedCdfMetadata(cdf, {
    ...metadata,
    accessRestriction: ROLODEX_INTERNAL_ACCESS_RESTRICTION,
  })
}

function withWorkflowGeneratedCdfMetadata(
  cdf: GeckoComponentDataFrame,
  metadata: ReusableWorkflowGeneratedCdfMetadata & {
    accessRestriction: typeof ROLODEX_INTERNAL_ACCESS_RESTRICTION | null
  }
): GeckoComponentDataFrame {
  const rawParams = (cdf.raw.data.params ?? {}) as Record<string, unknown>
  const params = {
    ...rawParams,
    [PLAN_REPAIR_GENERATED_BY_PARAM]: WORKFLOW_GENERATED_BY_PLAN_REPAIRS,
    ...(metadata.displayName?.trim()
      ? { [PLAN_REPAIR_DISPLAY_NAME_PARAM]: metadata.displayName.trim() }
      : {}),
    ...(metadata.originPlanId
      ? { [PLAN_REPAIR_ORIGIN_PLAN_ID_PARAM]: metadata.originPlanId }
      : {}),
  }

  return GeckoComponentDataFrame.CreateFromJSON({
    ...cdf.raw,
    access_restriction: metadata.accessRestriction,
    updated_at: new Date().toISOString(),
    data: {
      ...cdf.raw.data,
      params,
    },
  })
}

function getCdfParams(cdf: GeckoComponentDataFrame): Record<string, unknown> {
  return cdf.data.params as unknown as Record<string, unknown>
}
